const API_BASE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter | null = null;

export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

const DEFAULT_TIMEOUT_MS = 60_000;

async function authFetch(
  url: string,
  options: RequestInit = {},
  config?: { timeoutMs?: number },
): Promise<Response> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');

  if (getToken) {
    const token = await getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const timeoutMs = config?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: response.statusText }));
      throw new ApiError(response.status, body.error || response.statusText);
    }

    return response;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, 'Request timed out');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface ProjectSection {
  id: string;
  projectId: string;
  position: number;
  content: string;
  title: string | null;
  wordCount: number | null;
  wpmOverride: number | null;
  estimatedDurationMs: number | null;
  detectedTone: string | null;
  toneConfidence: number | null;
  positiveLocalStyles: string[] | null;
  negativeLocalStyles: string[] | null;
}

export interface Project {
  id: string;
  userId: string;
  title: string;
  rawContent: string | null;
  positiveGlobalStyles: string[] | null;
  negativeGlobalStyles: string[] | null;
  energy: number | null;
  aiSuggestedPositiveGlobalStyles: string[] | null;
  aiSuggestedNegativeGlobalStyles: string[] | null;
  aiSuggestedEnergy: number | null;
  globalWpm: number | null;
  rehearsalSkipped: boolean;
  sectionsAccepted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectWithSections extends Project {
  sections: ProjectSection[];
}

export interface ProjectListItem extends Project {
  sectionCount: number;
  totalDurationMs: number | null;
  musicStatus: 'none' | 'ready';
  pipelineStatus: 'draft' | 'needs_analysis' | 'needs_rehearsal' | 'needs_music' | 'ready';
  folderId: string | null;
  previewAudioUrl: string | null;
}

export interface Folder {
  id: string;
  userId: string;
  name: string;
  position: number;
  scriptCount: number;
  createdAt: string;
  updatedAt: string;
}

export async function fetchProjects(options?: {
  sortBy?: 'updatedAt' | 'createdAt' | 'title';
  sortDir?: 'asc' | 'desc';
  folderId?: string;
}): Promise<ProjectListItem[]> {
  const params = new URLSearchParams();
  if (options?.sortBy) params.set('sortBy', options.sortBy);
  if (options?.sortDir) params.set('sortDir', options.sortDir);
  if (options?.folderId) params.set('folderId', options.folderId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await authFetch(`/api/projects${query}`);
  const data = await response.json();
  return data.projects;
}

export async function duplicateProject(projectId: string): Promise<Project> {
  const response = await authFetch(`/api/projects/${projectId}/duplicate`, {
    method: 'POST',
  });
  const data = await response.json();
  return data.project;
}

export async function deleteProject(projectId: string): Promise<void> {
  await authFetch(`/api/projects/${projectId}`, { method: 'DELETE' });
}

export async function updateProject(
  projectId: string,
  data: {
    title?: string;
    folderId?: string | null;
    rehearsalSkipped?: boolean;
    sectionsAccepted?: boolean;
  },
): Promise<Project> {
  const response = await authFetch(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  const result = await response.json();
  return result.project;
}

export async function createProject(title: string, folderId?: string | null): Promise<Project> {
  const body: Record<string, unknown> = { title };
  if (folderId) body.folderId = folderId;
  const response = await authFetch('/api/projects', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  return data.project;
}

export async function fetchProject(projectId: string): Promise<ProjectWithSections> {
  const response = await authFetch(`/api/projects/${projectId}`);
  const data = await response.json();
  return data.project;
}

export interface MusicSuggestions {
  positiveGlobalStyles: string[];
  negativeGlobalStyles: string[];
  energy: number;
}

export async function analyzeProject(
  projectId: string,
): Promise<{ sections: ProjectSection[]; musicSuggestions?: MusicSuggestions }> {
  const response = await authFetch(
    `/api/projects/${projectId}/analyze`,
    { method: 'POST' },
    { timeoutMs: 120_000 },
  );
  return response.json();
}

export async function updateProjectContent(
  projectId: string,
  rawContent: string,
): Promise<Project> {
  const response = await authFetch(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify({ rawContent }),
  });
  const data = await response.json();
  return data.project;
}

export async function updateProjectMusicSettings(
  projectId: string,
  settings: {
    positiveGlobalStyles?: string[] | null;
    negativeGlobalStyles?: string[] | null;
    energy?: number | null;
  },
): Promise<Project> {
  const response = await authFetch(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
  const data = await response.json();
  return data.project;
}

export async function updateProjectGlobalWpm(
  projectId: string,
  globalWpm: number,
): Promise<Project> {
  const response = await authFetch(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify({ globalWpm }),
  });
  const data = await response.json();
  return data.project;
}

export async function updateSectionWpm(
  projectId: string,
  position: number,
  wpmOverride: number | null,
): Promise<ProjectSection> {
  const response = await authFetch(`/api/projects/${projectId}/sections/${position}`, {
    method: 'PATCH',
    body: JSON.stringify({ wpmOverride }),
  });
  const data = await response.json();
  return data.section;
}

export async function updateSectionMusicSettings(
  projectId: string,
  position: number,
  settings: {
    positiveLocalStyles?: string[] | null;
    negativeLocalStyles?: string[] | null;
    detectedTone?: string | null;
  },
): Promise<ProjectSection> {
  const response = await authFetch(`/api/projects/${projectId}/sections/${position}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
  const data = await response.json();
  return data.section;
}

export async function updateSectionTone(
  projectId: string,
  position: number,
  detectedTone: string,
): Promise<ProjectSection> {
  const response = await authFetch(`/api/projects/${projectId}/sections/${position}`, {
    method: 'PATCH',
    body: JSON.stringify({ detectedTone }),
  });
  const data = await response.json();
  return data.section;
}

export interface TimelineSection {
  id: string;
  position: number;
  title: string | null;
  startTimeMs: number;
  durationMs: number;
  wordCount: number;
  wpm: number;
}

export interface TimelineResponse {
  totalDurationMs: number;
  sections: TimelineSection[];
}

export async function fetchTimeline(projectId: string): Promise<TimelineResponse> {
  const response = await authFetch(`/api/projects/${projectId}/timeline`);
  return response.json();
}

export interface GenerationSectionStatus {
  id: string;
  position: number;
  title: string | null;
  generationStatus: 'pending' | 'analyzing_tone' | 'generating' | 'ready' | 'error';
  audioUrl: string | null;
  audioDurationMs: number | null;
  audioOffsetMs: number | null;
  partitionIndex: number | null;
  lastError: string | null;
}

export interface GenerationPartitionStatus {
  partitionIndex: number;
  audioUrl: string | null;
  audioDurationMs: number | null;
  musicProvider: string | null;
  status: 'pending' | 'generating' | 'ready' | 'error';
}

export interface GenerationStatus {
  id: string;
  status: 'pending' | 'tone_analysis' | 'generating_music' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  totalDurationMs: number | null;
  estimatedTimeRemainingMs: number | null;
  partitions: GenerationPartitionStatus[];
  sections: GenerationSectionStatus[];
}

export async function startGeneration(projectId: string): Promise<{ generationId: string }> {
  const response = await authFetch(`/api/projects/${projectId}/generate`, {
    method: 'POST',
  });
  return response.json();
}

export async function fetchGenerationStatus(
  projectId: string,
): Promise<{ generation: GenerationStatus | null }> {
  const response = await authFetch(`/api/projects/${projectId}/generation-status`);
  return response.json();
}

export async function cancelGeneration(projectId: string): Promise<void> {
  await authFetch(`/api/projects/${projectId}/generation/cancel`, {
    method: 'POST',
  });
}

export async function retrySection(projectId: string, sectionId: string): Promise<void> {
  await authFetch(`/api/projects/${projectId}/sections/${sectionId}/retry`, {
    method: 'POST',
  });
}

export async function regenerateSection(
  projectId: string,
  sectionId: string,
): Promise<{ generationId: string }> {
  const response = await authFetch(`/api/projects/${projectId}/sections/${sectionId}/regenerate`, {
    method: 'POST',
  });
  return response.json();
}

export interface Rehearsal {
  id: string;
  projectId: string;
  sectionTimings: Array<{ position: number; durationMs: number }>;
  totalDurationMs: number;
  createdAt: string;
}

export async function saveRehearsal(
  projectId: string,
  sectionTimings: Array<{ position: number; durationMs: number }>,
  totalDurationMs: number,
): Promise<Rehearsal> {
  const response = await authFetch(`/api/projects/${projectId}/rehearsals`, {
    method: 'POST',
    body: JSON.stringify({ sectionTimings, totalDurationMs }),
  });
  const data = await response.json();
  return data.rehearsal;
}

export async function getLatestRehearsal(projectId: string): Promise<Rehearsal | null> {
  try {
    const response = await authFetch(`/api/projects/${projectId}/rehearsals/latest`);
    const data = await response.json();
    return data.rehearsal;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function fetchTimelineWithSource(
  projectId: string,
  source: 'wpm' | 'rehearsal',
): Promise<TimelineResponse> {
  const response = await authFetch(`/api/projects/${projectId}/timeline?source=${source}`);
  return response.json();
}

// Usage API

export interface UsagePlanLimits {
  maxProjects: number | null;
  maxMonthlyMinutes: number | null;
  maxScriptDurationMinutes: number | null;
  generationRateLimitPerHour: number;
  planName: string;
  planDisplayName: string;
}

export interface UsageData {
  projectCount: number;
  generatedMinutesThisMonth: number;
  limits: UsagePlanLimits;
}

export async function fetchUsage(): Promise<UsageData> {
  const response = await authFetch('/api/usage');
  const data = await response.json();
  return data.usage;
}

// Admin API

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  isDefault: boolean;
  maxProjects: number | null;
  maxMonthlyMinutes: string | null;
  maxScriptDurationMinutes: string | null;
  maxSectionsPerProject: number | null;
  generationRateLimitPerHour: number;
  queuePriorityBoost: number;
  stripePriceId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlanInput {
  name: string;
  displayName: string;
  isDefault?: boolean;
  maxProjects?: number | null;
  maxMonthlyMinutes?: string | number | null;
  maxScriptDurationMinutes?: string | number | null;
  maxSectionsPerProject?: number | null;
  generationRateLimitPerHour?: number;
  queuePriorityBoost?: number;
  stripePriceId?: string | null;
  isActive?: boolean;
}

export interface SubscriptionWithDetails {
  id: string;
  userId: string;
  planId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  email: string;
  planName: string;
}

export async function fetchPlans(): Promise<Plan[]> {
  const response = await authFetch('/api/admin/plans');
  const data = await response.json();
  return data.plans;
}

export async function createPlan(data: PlanInput): Promise<Plan> {
  const response = await authFetch('/api/admin/plans', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const result = await response.json();
  return result.plan;
}

export async function updatePlan(id: string, data: Partial<PlanInput>): Promise<Plan> {
  const response = await authFetch(`/api/admin/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  const result = await response.json();
  return result.plan;
}

// Admin Users API

export interface AdminUser {
  id: string;
  email: string;
  clerkExternalId: string;
  createdAt: string;
  planName: string | null;
  planDisplayName: string | null;
  subscriptionStatus: string | null;
  projectCount: number;
  generatedMinutesThisMonth: number;
  lastProjectUpdatedAt: string | null;
  hasClerkAccount: boolean;
  hasStripeCustomer: boolean;
}

export async function fetchAdminUsers(options?: {
  limit?: number;
  cursor?: string;
  search?: string;
}): Promise<{ users: AdminUser[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.cursor) params.set('cursor', options.cursor);
  if (options?.search) params.set('search', options.search);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await authFetch(`/api/admin/users${query}`);
  return response.json();
}

export async function updateUserPlan(userId: string, planId: string): Promise<void> {
  await authFetch(`/api/admin/users/${userId}/plan`, {
    method: 'PUT',
    body: JSON.stringify({ planId }),
  });
}

export async function updateUserStatus(
  userId: string,
  action: 'activate' | 'cancel' | 'disable',
): Promise<void> {
  await authFetch(`/api/admin/users/${userId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ action }),
  });
}

export async function deleteUser(userId: string): Promise<void> {
  await authFetch(`/api/admin/users/${userId}`, {
    method: 'DELETE',
  });
}

export async function fetchSubscriptions(): Promise<SubscriptionWithDetails[]> {
  const response = await authFetch('/api/admin/subscriptions');
  const data = await response.json();
  return data.subscriptions;
}

// Billing API

export async function createCheckoutSession(planName: string): Promise<{ checkoutUrl: string }> {
  const response = await authFetch('/api/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ planName }),
  });
  return response.json();
}

export async function createPortalSession(): Promise<{ portalUrl: string }> {
  const response = await authFetch('/api/billing/customer-portal', {
    method: 'POST',
  });
  return response.json();
}

// Folders API

export async function fetchFolders(): Promise<Folder[]> {
  const response = await authFetch('/api/folders');
  const data = await response.json();
  return data.folders;
}

export async function createFolder(name: string): Promise<Folder> {
  const response = await authFetch('/api/folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
  const data = await response.json();
  return data.folder;
}

export async function updateFolder(folderId: string, name: string): Promise<Folder> {
  const response = await authFetch(`/api/folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
  const data = await response.json();
  return data.folder;
}

export async function deleteFolder(folderId: string): Promise<void> {
  await authFetch(`/api/folders/${folderId}`, { method: 'DELETE' });
}

// User Preferences API

export interface UserPreferences {
  advancedMode: boolean;
}

export async function fetchUserPreferences(): Promise<UserPreferences> {
  const response = await authFetch('/api/user/preferences');
  return response.json();
}

export async function updateUserPreferences(
  data: Partial<UserPreferences>,
): Promise<UserPreferences> {
  const response = await authFetch('/api/user/preferences', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Sync API

export async function getWsToken(): Promise<string> {
  const response = await authFetch('/api/auth/ws-token');
  const data = await response.json();
  return data.token;
}
